import { Box3, Plane, Vector3 } from 'three'

interface RoofAssignment {
  /** Elements of this (effective) type are assigned to `storey` regardless of z. */
  type: string
  storey: string
}

/**
 * Assign each element to a storey. Roof elements (`roof.type`) always go to the roof storey;
 * every other element goes to the first storey whose center z-plane intersects its box (via
 * three.js `Plane.intersectsBox` — the plane sits at the center of each storey's configured
 * boundaries). Elements not crossed by any storey plane (and not roofs) are left unassigned.
 */
export function computeAutoStoreyAssignments(
  guidToBox: Record<string, Box3>,
  storeyBoundaries: Record<string, [number, number]>,
  guidToType: Record<string, string>,
  roof: RoofAssignment,
): { elementId: string; storey: string }[] {
  const storeyPlanes = Object.entries(storeyBoundaries).map(([storey, [lower, upper]]) => ({
    storey,
    // Horizontal plane z = center  ⇔  (0,0,1)·x + constant = 0 with constant = -center.
    plane: new Plane(new Vector3(0, 0, 1), -(lower + upper) / 2),
  }))

  const assignments: { elementId: string; storey: string }[] = []
  Object.entries(guidToBox).forEach(([elementId, box]) => {
    if (guidToType[elementId] === roof.type) {
      assignments.push({ elementId, storey: roof.storey })
      return
    }
    const match = storeyPlanes.find(({ plane }) => plane.intersectsBox(box))
    if (match) assignments.push({ elementId, storey: match.storey })
  })

  return assignments
}
